import type {
  ActivityEntry,
  IndexedFieldRow,
  PreTransferTieout,
  SectionKind,
  TransferActivity,
  TransferField,
} from "./PreviewImportModal.types";

const SIMPLE_SECTION_CHILD_KEYS = new Set(["ActivityFields", "Fields", "Tables"]);

const SECTION_LABEL_MAP: Record<string, string> = {
  GeneralFields: "General",
  Activities: "Activity",
  Statements: "Statement",
};

export const getSectionLabel = (sectionKey: string) => SECTION_LABEL_MAP[sectionKey] ?? sectionKey;

export const stringifyValue = (value: string | number | null | undefined) =>
  value === null || typeof value === "undefined" ? "" : String(value);

export const getFieldDataLabel = (fieldId: string | undefined, fallback: string | undefined) =>
  String(fieldId || fallback || "");

const isObject = (value: any) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const getActivityFields = (activity: TransferField[] | TransferActivity | any): TransferField[] => {
  if (Array.isArray(activity)) return activity;
  if (Array.isArray(activity?.Fields)) return activity.Fields;
  return [];
};

const getActivityLabel = (activityKey: string, activity: TransferActivity | any) => {
  const number = String(activity?.ActivityNumber ?? "").trim();
  const description = String(activity?.ActivityDescription ?? "").trim();
  if (number && description) return `${activityKey}: ${number} - ${description}`;
  if (description) return `${activityKey}: ${description}`;
  if (number) return `${activityKey}: ${number}`;
  return activityKey;
};

export const getSimpleSectionRows = (sectionValue: any): TransferField[] => {
  if (Array.isArray(sectionValue)) return sectionValue;
  if (Array.isArray(sectionValue?.ActivityFields)) return sectionValue.ActivityFields;
  if (Array.isArray(sectionValue?.Fields)) return sectionValue.Fields;
  return [];
};

export const getActivityEntries = (sectionValue: any): ActivityEntry[] => {
  if (!isObject(sectionValue)) return [];
  return Object.entries(sectionValue)
    .filter(([activityKey]) => !SIMPLE_SECTION_CHILD_KEYS.has(activityKey))
    .map(([activityKey, activityValue]: [string, any]) => ({
      key: activityKey,
      label: getActivityLabel(activityKey, activityValue),
      fields: getActivityFields(activityValue),
    }))
    .filter((activity) => activity.fields.length > 0);
};

export const getSectionKind = (sectionValue: any): SectionKind => {
  if (Array.isArray(sectionValue)) return "simple";
  if (
    isObject(sectionValue) &&
    (
      "ActivityFields" in sectionValue ||
      "Fields" in sectionValue ||
      "Tables" in sectionValue
    )
  ) {
    return "simple";
  }
  if (
    isObject(sectionValue) &&
    Object.entries(sectionValue).some(([key, value]) =>
      !SIMPLE_SECTION_CHILD_KEYS.has(key) &&
      (Array.isArray(value) || isObject(value))
    )
  ) {
    return "activity";
  }
  if (getSimpleSectionRows(sectionValue).length > 0) return "simple";
  if (getActivityEntries(sectionValue).length > 0) return "activity";
  return "empty";
};

export const getSectionRowCount = (sectionValue: any) => {
  const sectionKind = getSectionKind(sectionValue);
  if (sectionKind === "simple") return getSimpleSectionRows(sectionValue).length;
  if (sectionKind === "activity") {
    return getActivityEntries(sectionValue).reduce(
      (rowCount, activity) => Math.max(rowCount, activity.fields.length),
      0,
    );
  }
  return 0;
};

export const getSectionEntries = (entity: Record<string, any> | null) => {
  if (!entity) return [];
  return Object.entries(entity).filter(([, value]) => getSectionRowCount(value) > 0);
};

export const getMatrixRow = (activities: ActivityEntry[], rowIndex: number) =>
  activities.find((activity) => activity.fields[rowIndex])?.fields[rowIndex] ?? null;

const getSearchText = (value: string | number | null | undefined) =>
  stringifyValue(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getSearchTerms = (searchQuery: string) =>
  getSearchText(searchQuery).split(/\s+/).filter(Boolean);

const fieldMatchesSearch = (row: TransferField | undefined, searchTerms: string[]) => {
  if (!searchTerms.length) return true;
  if (!row) return false;

  const rowText = [
    row.ParentFieldId,
    row.ParentFieldDescription,
    row.ParentValue,
    row.ChildFieldId,
    row.ChildFieldDescription,
    row.ChildValue,
  ].map(getSearchText).join(" ");

  return searchTerms.every((term) => rowText.includes(term));
};

export const getFilteredSimpleRows = (rows: TransferField[], searchQuery: string): IndexedFieldRow[] => {
  const searchTerms = getSearchTerms(searchQuery);
  return rows
    .map((row, rowIndex) => ({ row, rowIndex }))
    .filter(({ row }) => fieldMatchesSearch(row, searchTerms));
};

export const getFilteredActivityRowIndexes = (activities: ActivityEntry[], searchQuery: string) => {
  const searchTerms = getSearchTerms(searchQuery);
  const maxRows = Math.max(0, ...activities.map((activity) => activity.fields.length));
  const rowIndexes = Array.from({ length: maxRows }, (_, rowIndex) => rowIndex);

  if (!searchTerms.length) return rowIndexes;

  return rowIndexes.filter((rowIndex) =>
    activities.some((activity) =>
      searchTerms.every((term) => getSearchText(activity.label).includes(term)) ||
      fieldMatchesSearch(activity.fields[rowIndex], searchTerms)
    )
  );
};

export const updateSimpleSectionValue = (
  data: PreTransferTieout,
  entityKey: string,
  sectionKey: string,
  rowIndex: number,
  value: string,
) => {
  const next: PreTransferTieout = { ...data };
  const entity = { ...(next[entityKey] ?? {}) };
  const sectionValue = entity[sectionKey];

  if (Array.isArray(sectionValue)) {
    const rows = [...sectionValue];
    rows[rowIndex] = { ...rows[rowIndex], ChildValue: value };
    entity[sectionKey] = rows;
  } else if (Array.isArray(sectionValue?.ActivityFields)) {
    entity[sectionKey] = {
      ...sectionValue,
      ActivityFields: sectionValue.ActivityFields.map((row: TransferField, idx: number) =>
        idx === rowIndex ? { ...row, ChildValue: value } : row
      ),
    };
  } else if (Array.isArray(sectionValue?.Fields)) {
    entity[sectionKey] = {
      ...sectionValue,
      Fields: sectionValue.Fields.map((row: TransferField, idx: number) =>
        idx === rowIndex ? { ...row, ChildValue: value } : row
      ),
    };
  }

  next[entityKey] = entity;
  return next;
};

export const updateActivitySectionValue = (
  data: PreTransferTieout,
  entityKey: string,
  sectionKey: string,
  activityKey: string,
  rowIndex: number,
  value: string,
) => {
  const next: PreTransferTieout = { ...data };
  const entity = { ...(next[entityKey] ?? {}) };
  const sectionValue = { ...(entity[sectionKey] ?? {}) };
  const activityValue = sectionValue[activityKey];

  if (Array.isArray(activityValue)) {
    const rows = [...activityValue];
    rows[rowIndex] = { ...rows[rowIndex], ChildValue: value };
    sectionValue[activityKey] = rows;
  } else {
    const rows = [...getActivityFields(activityValue)];
    rows[rowIndex] = { ...rows[rowIndex], ChildValue: value };
    sectionValue[activityKey] = { ...(activityValue ?? {}), Fields: rows };
  }

  entity[sectionKey] = sectionValue;
  next[entityKey] = entity;
  return next;
};
